import { IconBrandAws } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { CopyInput } from "@/components/copy-input";
import { DeleteEpisodeDialog } from "@/components/forms/episodes/delete";
import { AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
	FormCard,
	FormCardAlert,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { InputGroupAddon } from "@/components/ui/input-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getEpisodeStatus } from "@/enums/episode-status";
import { formatDate } from "@/lib/format";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/episodes/$episodeId/settings")({
	component: Page,
	params: z.object({
		episodeId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.show.queryOptions({
				input: {
					episodeId: params.episodeId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: episode } = useSuspenseQuery(
		orpc.episodes.show.queryOptions({
			input: {
				episodeId: params.episodeId,
			},
		}),
	);

	return (
		<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
			<FormCard className="col-span-12">
				<FormCardHeader>
					<FormCardTitle>Episode Meta</FormCardTitle>
				</FormCardHeader>
				<FormCardContent>
					<FieldGroup>
						<Field className="lg:col-span-6">
							<FieldLabel>ID</FieldLabel>
							<CopyInput value={episode.ID.toString()} readOnly />
						</Field>
						<Field className="lg:col-span-6">
							<FieldLabel>UID</FieldLabel>
							<CopyInput value={episode.UID.toString()} readOnly />
						</Field>
						<Field className="lg:col-span-6">
							<FieldLabel>Status</FieldLabel>
							<CopyInput value={getEpisodeStatus(episode.StatusID).label} readOnly />
						</Field>
						<Field className="lg:col-span-6">
							<FieldLabel>URL slug</FieldLabel>
							<CopyInput value={episode.Channel.UrlTitle} readOnly>
								<InputGroupAddon align="inline-end">
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger
												render={
													<Button
														variant="link"
														size="icon-sm"
														nativeButton={false}
														render={
															<a
																href={`https://s3.console.aws.amazon.com/s3/buckets/${import.meta.env.VITE_AWS_S3_BUCKET}?region=us-east-1&prefix=${episode.Channel.UrlTitle}&delimiter=/`}
																target="_blank"
																rel="noopener"
															/>
														}
													>
														<IconBrandAws />
													</Button>
												}
											/>
											<TooltipContent>Open in S3 Console</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</InputGroupAddon>
							</CopyInput>
							<FieldDescription>
								This is the URL-friendly version of the channel title, used in S3 buckets to store
								files but also for constructing the channel's public URL.
							</FieldDescription>
						</Field>
						<Field className="col-start-1 lg:col-span-6">
							<FieldLabel>Updated Date</FieldLabel>
							<CopyInput value={formatDate(episode.UpdatedDate)} readOnly />
						</Field>
						<Field className="lg:col-span-6">
							<FieldLabel>Created Date</FieldLabel>
							<CopyInput value={formatDate(episode.CreatedDate)} readOnly />
						</Field>
					</FieldGroup>
				</FormCardContent>
			</FormCard>
			<FormCard className="col-span-12">
				<FormCardHeader>
					<FormCardTitle>Delete Episode</FormCardTitle>
				</FormCardHeader>
				<FormCardAlert variant="destructive">
					<AlertTitle>Are you sure you want to delete this episode?</AlertTitle>
					<AlertDescription>
						This action cannot be undone. This will permanently delete the episode and all of its
						data.
					</AlertDescription>
					<AlertAction>
						<DeleteEpisodeDialog episodeId={episode.ID}>
							<Button variant="destructive">Delete Episode</Button>
						</DeleteEpisodeDialog>
					</AlertAction>
				</FormCardAlert>
			</FormCard>
		</div>
	);
}
